// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from '@/redux';
import debounce from 'lodash/debounce';

// Components
import { withErrorBoundary } from '@/views/components/errors';
import ScreenerSearch from '@/views/components/search/screener/ScreenerSearch';
import FilterButton from '@/views/components/search/screener/FilterButton';
import ScreenerFilters from '@/views/components/search/screener/ScreenerFilters';

// Selectors
import { selectAdvancedSearchShelfStatusedFund } from '@/redux/modules/shelf-management/selectors';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { searchAllFunds, searchAllBenchmarks, loadSearchFilters as loadFiltersAction } from '@/redux/modules/advanced-search';

// Utils
import { ADVANCED_SEARCH_OPTIONS } from '@/utils/constants';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';
import { formatMessage } from '@/utils/i18n';
import { populateFundFilters, populateBenchmarkFilters } from '@/utils/advanced-search';
import { AdvancedSearchResults } from '@/views/components/search';

// Types
import SearchOptions from '@/types/search-options';
import FilterType from '@/types/filter-type';
import FundFiltersData from '@/types/fund-filters-data';
import BenchmarkFiltersData from '@/types/benchmark-filters-data';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { Typography } from '@capintel/component-lib/typography';

type DispatchProps = {
  search: (options: SearchOptions) => void;
  loadSearchFilters: () => void;
};

type StateProps = {
  filters: FilterType[];
  loadingFilters: boolean;
  preLoadQuery: string;
};

type OwnProps = {
  instrumentType: ADVANCED_SEARCH_OPTIONS;
  insideModal?: boolean;
};

type Props = DispatchProps & StateProps & OwnProps;

/**
 * Generic screener content component.
 * Currently supports: Funds
 * Future: Benchmarks, Stocks, Bonds, Altsgp
 */
const ScreenerContent = ({ instrumentType, insideModal, filters, loadingFilters, preLoadQuery, search, loadSearchFilters }: Props) => {
  const [isFilterActive, setIsFilterActive] = React.useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState(preLoadQuery || '');
  const [activeFilters, setActiveFilters] = React.useState<Record<string, unknown>>({});

  // Debounced search function that combines query and filters
  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string, currentFilters: Record<string, unknown>) => {
        search({ ...currentFilters, query });
      }, SEARCH_DEBOUNCE_TIME),
    [search],
  );

  React.useEffect(() => {
    loadSearchFilters();
  }, [loadSearchFilters]);

  React.useEffect(() => {
    setActiveFilters({});
    setSearchQuery('');
    setActiveFiltersCount(0);
    setIsFilterActive(false);
  }, [instrumentType]);

  const getSubtitleKey = () => {
    switch (instrumentType) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS:
        return 'Screener.Funds.Title';
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS:
        return 'Screener.Benchmarks.Title';
      case ADVANCED_SEARCH_OPTIONS.STOCKS:
        return 'Screener.Stocks.Title';
      case ADVANCED_SEARCH_OPTIONS.BONDS:
        return 'Screener.Bonds.Title';
      default:
        return 'Screener.Funds.Title';
    }
  };

  const handleFilterClick = () => {
    setIsFilterActive(!isFilterActive);
  };

  const handleSearch = (options: SearchOptions) => {
    search(options);
  };

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    // Search as user types with debouncing, including active filters
    debouncedSearch(value, activeFilters);
  };

  const handleFiltersChange = (updatedFilters: Record<string, unknown>) => {
    setActiveFilters(updatedFilters);
  };

  return (
    <div className="screener-content">
      <div className="screener-header">
        <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
          {formatMessage(getSubtitleKey())}
        </Typography>
        <div className="screener-controls">
          <ScreenerSearch instrumentType={instrumentType} searchQuery={searchQuery} onSearchChange={handleSearchQueryChange} />
          <div className="screener-actions">
            <FilterButton onClick={handleFilterClick} activeFiltersCount={activeFiltersCount} isActive={isFilterActive} />
          </div>
        </div>
        {isFilterActive && (
          <ScreenerFilters
            filters={filters}
            loadingFilters={loadingFilters}
            preLoadQuery={searchQuery}
            onSearch={handleSearch}
            onFilterCountChange={setActiveFiltersCount}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
          />
        )}
      </div>
      <div className="screener-body">
        <AdvancedSearchResults instrumentType={instrumentType} className="all-items" useWindowScrollAncestor={!insideModal} />
      </div>
    </div>
  );
};

const mapStateToProps = (state: ReduxState, ownProps: OwnProps): StateProps => {
  let filtersData: FundFiltersData | BenchmarkFiltersData;
  let filters: FilterType[];

  switch (ownProps.instrumentType) {
    case ADVANCED_SEARCH_OPTIONS.FUNDS:
      filtersData = selectAdvancedSearchShelfStatusedFund(state.advancedSearch).filtersData;
      filters = populateFundFilters(filtersData, state.auth.user || undefined);
      break;
    case ADVANCED_SEARCH_OPTIONS.BENCHMARKS:
      filtersData = state.advancedSearch.benchmark.filtersData;
      filters = populateBenchmarkFilters(filtersData);
      break;
    default:
      filtersData = {} as FundFiltersData;
      filters = [];
  }

  return {
    loadingFilters: state.advancedSearch.loadingFilters,
    preLoadQuery: state.advancedSearch.preLoadQuery,
    filters,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps): DispatchProps => {
  let searchAction: (options: SearchOptions) => void;

  switch (ownProps.instrumentType) {
    case ADVANCED_SEARCH_OPTIONS.FUNDS:
      searchAction = (options: SearchOptions) => dispatch(searchAllFunds(options));
      break;
    case ADVANCED_SEARCH_OPTIONS.BENCHMARKS:
      searchAction = (options: SearchOptions) => dispatch(searchAllBenchmarks(options));
      break;
    // TODO: Add searchAllStocks and searchAllBonds when available
    default:
      searchAction = () => {};
  }

  return {
    search: searchAction,
    loadSearchFilters: () => dispatch(loadFiltersAction()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(ScreenerContent));
